import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuList,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { isNil, orderBy } from "lodash-es";
import { useCallback, useMemo, useState } from "react";

import InlineUser from "@/common/components/Entity/User/InlineUser";
import {
  AutomationCreateMode,
  GeneralStageHistoryDtoOfDamageCostEvaluationStage,
  GeneralStageHistoryItemDtoOfDamageCostEvaluationStage,
  GeneralStageInfoDtoOfDamageCostEvaluationStage,
} from "@/core/api/generated/v1";

import { DatetimeHelper } from "../helpers/datetime";
import { TextHelper } from "../helpers/text";
import { TypeHelper } from "../helpers/type";
import { ApiEnumName, ApiEnumValue, enumService } from "../services/enum";
import AppBadge from "./Badge/AppBadge";
import AppDropdownButton from "./Button/AppDropdownButton";
import AppIconButton from "./Button/AppIconButton";
import Datetime from "./Datetime/Datetime";
import InlineApiEnumValue from "./Enum/InlineApiEnumValue";
import FieldValue from "./Form/Display/FieldValue";
import AppIcon from "./Icons/AppIcon";
import AppMenuItem from "./Menu/AppMenuItem";
import HeaderMenuItem from "./Menu/HeaderMenuItem";
import ConfirmationModal from "./Modals/ConfirmationModal";
import AppPopover from "./Popover/AppPopover";
import AppPopoverContent from "./Popover/AppPopoverContent";
import AppTypography from "./Text/AppTypography";

// make OpenAPI types generic
interface GeneralStageHistoryItemDto<TStageEnumName extends ApiEnumName> extends Omit<
  GeneralStageHistoryItemDtoOfDamageCostEvaluationStage,
  "stage"
> {
  stage?: ApiEnumValue<TStageEnumName> | null;
}
interface GeneralStageInfoDto<TStageEnumName extends ApiEnumName> extends Omit<
  GeneralStageInfoDtoOfDamageCostEvaluationStage,
  "stage"
> {
  stage?: ApiEnumValue<TStageEnumName> | null;
}
interface GeneralStageHistoryDto<TStageEnumName extends ApiEnumName> extends Omit<
  GeneralStageHistoryDtoOfDamageCostEvaluationStage,
  "stage" | "items" | "allStagesInfo"
> {
  stage?: ApiEnumValue<TStageEnumName> | null;
  items?: Array<GeneralStageHistoryItemDto<TStageEnumName>> | null;
  allStagesInfo?: Array<GeneralStageInfoDto<TStageEnumName>> | null;
}

interface Props<TStageEnumName extends ApiEnumName> {
  stageEnumType: TStageEnumName;
  stageHistory?: GeneralStageHistoryDto<TStageEnumName>;
  withUpdateButtons?: boolean;
  withUpdateConfirmation?: boolean;
  excludeUpdateFor?: ApiEnumValue<TStageEnumName>[];
  onUpdateStage?: (newStage: ApiEnumValue<TStageEnumName>) => void | Promise<void>;
  sx?: SxProps<Theme>;
}

/** Displays stage history with select new stage buttons. */
export default function GeneralStageHistory<TStageEnumName extends ApiEnumName>({
  stageEnumType,
  stageHistory,
  withUpdateButtons = false,
  withUpdateConfirmation = true,
  excludeUpdateFor,
  onUpdateStage,
  sx,
}: Props<TStageEnumName>) {
  const [isStageUpdatingMap, setIsStageUpdatingMap] = useState<
    Partial<Record<ApiEnumValue<TStageEnumName>, boolean>>
  >({});
  const [tempNewStage, setTempNewStage] = useState<ApiEnumValue<TStageEnumName> | undefined>(
    undefined,
  );
  const [isUpdateConfirmationModalOpen, setIsUpdateConfirmationModalOpen] = useState(false);

  const historyItems = useMemo(
    () => stageHistory?.items && orderBy(stageHistory.items, (x) => x.sortOrder, "asc"),
    [stageHistory],
  );
  const currentStageInfo = useMemo(
    () => stageHistory?.allStagesInfo?.find((x) => x.stage === stageHistory?.stage),
    [stageHistory],
  );
  const possibleNewStagesInfo = useMemo(
    () =>
      stageHistory?.allStagesInfo &&
      orderBy(stageHistory.allStagesInfo, (x) => x.sortOrder, "asc")
        .filter((x) => x.sortOrder! > stageHistory.sortOrder!)
        .filter((x) => (excludeUpdateFor ? !excludeUpdateFor.includes(x.stage!) : true)),
    [stageHistory, excludeUpdateFor],
  );

  const handleUpdateStage = useCallback(
    async (newStage: ApiEnumValue<TStageEnumName>) => {
      if (stageHistory && stageHistory.stage !== newStage) {
        try {
          setIsStageUpdatingMap({
            ...isStageUpdatingMap,
            [newStage]: true,
          });
          onUpdateStage && (await onUpdateStage(newStage));
          setTempNewStage(undefined);
        } finally {
          setIsStageUpdatingMap({
            ...isStageUpdatingMap,
            [newStage]: false,
          });
        }
      }
    },
    [stageHistory, onUpdateStage],
  );

  return (
    <Box sx={sx}>
      <List dense>
        {historyItems?.map((item, i) => (
          <ListItem key={i}>
            <ListItemIcon>
              <Tooltip
                title={
                  <>
                    <Box>
                      {item.isEnded || item.isLast ? "Stage is completed" : "Stage is pending"}.
                    </Box>

                    {!isNil(item.startCount) && (
                      <Box>
                        Has been started {item.startCount}{" "}
                        {TextHelper.pluralizeManual("time", item.startCount, "times")}.
                      </Box>
                    )}
                  </>
                }
              >
                <AppBadge
                  badgeContent={
                    item.startCount && item.startCount >= 2 ? item.startCount : undefined
                  }
                  color='default'
                  showZero={false}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <AppIcon
                    of={item.isEnded || item.isLast ? "done" : "pending"}
                    fontSize='medium'
                  />
                </AppBadge>
              </Tooltip>
            </ListItemIcon>

            <ListItemText
              primary={
                <Typography component='span' variant={item.isCurrent ? "subtitle1" : "body1"}>
                  <Box>
                    <InlineApiEnumValue type={stageEnumType} value={item.stage} withHelperTooltip />

                    {item.startMode === AutomationCreateMode.Automatic && (
                      <Chip
                        sx={{ ml: 0.5 }}
                        variant='outlined'
                        color='default'
                        size='extraSmall'
                        label={
                          <AppTypography
                            decoration={{ variant: "helpText" }}
                            tooltipProps={{
                              title: "The stage was started automatically by the system.",
                            }}
                          >
                            Auto
                          </AppTypography>
                        }
                      />
                    )}

                    {/* Stage details popover */}
                    <AppPopover
                      hoverBehavior={{ closeBehavior: "onTriggerOrContentMouseLeave" }}
                      trigger={
                        <AppIconButton sx={{ ml: 0.5 }} variant='text' size='extraSmall'>
                          <AppIcon of='info' />
                        </AppIconButton>
                      }
                    >
                      <AppPopoverContent
                        sx={{
                          minWidth: { xxs: "90vw", md: 400, lg: 700 },
                          maxHeight: { xxs: "80vh" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xxs: "repeat(1, 1fr)",
                              md: "repeat(2, 1fr)",
                            },
                            gridTemplateRows: "auto",
                            rowGap: 2,
                            columnGap: 2,
                          }}
                        >
                          <FieldValue label='Stage' isEmpty={TypeHelper.isEmpty(item.stage)}>
                            <InlineApiEnumValue
                              type={stageEnumType}
                              value={item.stage}
                              withHelperTooltip
                            />

                            {item.isCurrent && (
                              <Chip
                                sx={{ ml: 1 }}
                                variant='outlined'
                                color='default'
                                size='extraSmall'
                                label='Current stage'
                              />
                            )}
                          </FieldValue>
                        </Box>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xxs: "repeat(1, 1fr)",
                              md: "repeat(2, 1fr)",
                            },
                            gridTemplateRows: "auto",
                            rowGap: 2,
                            columnGap: 2,
                          }}
                        >
                          <Stack spacing={1}>
                            {item.firstStartedAt !== item.startedAt && (
                              <FieldValue
                                label='First started at'
                                isEmpty={TypeHelper.isNil(item.firstStartedAt)}
                              >
                                <Datetime datetime={item.firstStartedAt} />
                              </FieldValue>
                            )}

                            <FieldValue
                              label='Started at'
                              isEmpty={TypeHelper.isNil(item.startedAt)}
                            >
                              <Datetime datetime={item.startedAt} />
                            </FieldValue>

                            <FieldValue
                              label='Started by'
                              isEmpty={TypeHelper.isNil(item.startedBy)}
                            >
                              <InlineUser
                                userId={item.startedBy}
                                withAvatar
                                withText
                                withLink
                                withPoper={false}
                              />
                            </FieldValue>

                            <FieldValue
                              label='Start reason'
                              isEmpty={TypeHelper.isEmpty(item.startReason)}
                            >
                              {item.startReason}
                            </FieldValue>

                            <FieldValue
                              label='Start mode'
                              isEmpty={TypeHelper.isEmpty(item.startMode)}
                            >
                              <InlineApiEnumValue
                                type='AutomationCreateMode'
                                value={item.startMode}
                                withHelperTooltip
                              />
                            </FieldValue>
                          </Stack>

                          <Stack spacing={1}>
                            {item.firstEndedAt !== item.endedAt && (
                              <FieldValue
                                label='First ended at'
                                isEmpty={TypeHelper.isNil(item.firstEndedAt)}
                              >
                                <Datetime datetime={item.firstEndedAt} />
                              </FieldValue>
                            )}

                            <FieldValue label='Ended at' isEmpty={TypeHelper.isNil(item.endedAt)}>
                              <Datetime datetime={item.endedAt} />
                            </FieldValue>

                            <FieldValue label='Ended by' isEmpty={TypeHelper.isNil(item.endedBy)}>
                              <InlineUser
                                userId={item.endedBy}
                                withAvatar
                                withText
                                withLink
                                withPoper={false}
                              />
                            </FieldValue>

                            <FieldValue
                              label='End reason'
                              isEmpty={TypeHelper.isEmpty(item.endReason)}
                            >
                              {item.endReason}
                            </FieldValue>

                            <FieldValue label='End mode' isEmpty={TypeHelper.isEmpty(item.endMode)}>
                              <InlineApiEnumValue
                                type='AutomationCreateMode'
                                value={item.endMode}
                                withHelperTooltip
                              />
                            </FieldValue>
                          </Stack>
                        </Box>
                      </AppPopoverContent>
                    </AppPopover>
                  </Box>
                </Typography>
              }
              secondary={
                <Stack>
                  {!item.isLast && (
                    <Typography component='span' variant='body2'>
                      <Datetime datetime={item.startedAt} />
                      {" - "}
                      <Datetime datetime={item.endedAt} />
                      {item.startedAt &&
                        item.endedAt &&
                        ` (${DatetimeHelper.humanizeDateRangeDuration(
                          item.startedAt,
                          item.endedAt,
                          { isSuffix: false },
                        )})`}
                    </Typography>
                  )}

                  {item.isLast && (
                    <Typography component='span' variant='body2'>
                      <Datetime datetime={item.startedAt} withDurationFromNow />
                    </Typography>
                  )}

                  {item.startReason && (
                    <Typography component='span' variant='body2'>
                      Start reason: {item.startReason}
                    </Typography>
                  )}

                  {item.endReason && (
                    <Typography component='span' variant='body2'>
                      End reason: {item.endReason}
                    </Typography>
                  )}
                </Stack>
              }
            />
          </ListItem>
        ))}
      </List>

      {/* Update buttons as list */}
      {/* {withUpdateButtons && (
        <Stack direction={{ xs: "column", sm: "column", md: "row" }} spacing={1}>
          {possibleNewStagesInfo?.map((info, index) => (
            <Tooltip
              key={index}
              title={`Start '${enumService.getEnumValueName(stageEnumType, info.stage)}' stage`}
            >
              <Button
                loading={isStageUpdatingMap[info.stage!]}
                variant='contained'
                onClick={() => {
                  if (withUpdateConfirmation) {
                    setTempNewStage(info.stage!);
                    setIsUpdateConfirmationModalOpen(true);
                  } else {
                    handleUpdateStage(info.stage!);
                  }
                }}
              >
                <InlineApiEnumValue type={stageEnumType} value={info.stage} />
              </Button>
            </Tooltip>
          ))}
        </Stack>
      )} */}

      {/* Update buttons as dropdown */}
      {withUpdateButtons && !currentStageInfo?.isFinal && (
        <Box>
          <AppDropdownButton
            size='small'
            autoCloseOnClick={false}
            buttonProps={{
              // color: "inherit",
              color: "text",
              size: "medium",
              variant: "outlined",
              // sx: { p: 0, minWidth: "auto", height: "auto" },
              startIcon: <AppIcon of='edit' />,
              tooltipProps: { title: "Update stage" },
            }}
            dropdownContentWrapperProps={{
              minWidth: 200,
              maxHeight: {
                xxs: "90vh",
                md: 300,
              },
            }}
            onOpen={() => {
              // setIsDropdownOpen(true);
            }}
            dropdownContent={(params) => (
              <Stack spacing={1}>
                <MenuList>
                  {!TypeHelper.isEmpty(possibleNewStagesInfo) && (
                    <HeaderMenuItem primaryTitle='Update stage to:' />
                  )}
                  {TypeHelper.isEmpty(possibleNewStagesInfo) && (
                    <HeaderMenuItem
                      primaryTitle={`Can't update stage when it's ${enumService.getEnumValueName(
                        stageEnumType,
                        stageHistory?.stage,
                      )}.`}
                    />
                  )}

                  {possibleNewStagesInfo?.map((info, i) => (
                    <AppMenuItem
                      key={i}
                      onClick={async () => {
                        if (withUpdateConfirmation) {
                          setTempNewStage(info.stage!);
                          setIsUpdateConfirmationModalOpen(true);
                        } else {
                          handleUpdateStage(info.stage!);
                        }
                      }}
                    >
                      <ListItemIcon>
                        <AppIcon of='data' />
                      </ListItemIcon>
                      <ListItemText>
                        <InlineApiEnumValue
                          type={stageEnumType}
                          value={info.stage}
                          withHelperTooltip
                        />
                      </ListItemText>
                    </AppMenuItem>
                  ))}
                </MenuList>
              </Stack>
            )}
          >
            <InlineApiEnumValue
              type={stageEnumType}
              value={stageHistory?.stage}
              withHelperTooltip={false}
            />
          </AppDropdownButton>
        </Box>
      )}

      <ConfirmationModal
        title={`Do you confirm stage update?`}
        body={
          <>
            You are going to update stage from{" "}
            <strong>
              <InlineApiEnumValue type={stageEnumType} value={stageHistory?.stage} />
            </strong>{" "}
            to{" "}
            <strong>
              <InlineApiEnumValue type={stageEnumType} value={tempNewStage} />
            </strong>
            .
          </>
        }
        open={isUpdateConfirmationModalOpen}
        onClose={() => setIsUpdateConfirmationModalOpen(false)}
        onCancel={() => {
          setIsUpdateConfirmationModalOpen(false);
          setTempNewStage(undefined);
        }}
        onConfirm={async () => {
          tempNewStage && (await handleUpdateStage(tempNewStage));
          setIsUpdateConfirmationModalOpen(false);
        }}
      />
    </Box>
  );
}
